import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  CircularProgress,
  Stack,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Avatar,
  Switch,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Category as CategoryIcon,
  Fastfood as DishIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Store as StoreIcon,
  ShoppingCart as OrderTypeIcon,
  FileCopy as CopyIcon,
  RestaurantMenu as RestaurantMenuIcon,
} from '@mui/icons-material';
import { useAppStore } from '../store/appStore';
import { productService } from '../services/productService';
import { colors } from '../theme/theme';
import toast from 'react-hot-toast';

const MenuCategories = () => {
  const navigate = useNavigate();
  const { menuId } = useParams();
  const { branch: selectedBranch } = useAppStore();

  const [menuType, setMenuType] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [togglingItem, setTogglingItem] = useState(null);
  const [copyCategoryDialogOpen, setCopyCategoryDialogOpen] = useState(false);
  const [copyCategorySource, setCopyCategorySource] = useState(null);
  const [copyCategoryTargetMenu, setCopyCategoryTargetMenu] = useState('');
  const [copyCategoryOperation, setCopyCategoryOperation] = useState('copy');
  const [copyingCategory, setCopyingCategory] = useState(false);
  const [availableMenusForCopy, setAvailableMenusForCopy] = useState([]);

  useEffect(() => {
    if (!menuId) {
      navigate('/dish-access');
      return;
    }

    setLoading(true);
    setError('');

    const loadData = async () => {
      try {
        // First fetch the menu type details
        const menuResponse = await productService.getMenu(menuId);
        if (!menuResponse?.data?.success) {
          throw new Error('Menu type not found');
        }

        const menuData = menuResponse.data.data;
        setMenuType(menuData);

        // Then fetch categories filtered by this menu
        const categoryResponse = await productService.getCategories({ menu: menuId });

        if (categoryResponse?.data?.success) {
          setCategories(categoryResponse.data.data || []);
        } else {
          throw new Error(categoryResponse?.data?.message || 'Unable to load categories');
        }
      } catch (fetchError) {
        console.error('Error loading menu categories:', fetchError);
        setError(fetchError.message || 'Failed to load menu categories.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [menuId, navigate, selectedBranch]);

  const refreshCategories = async () => {
    try {
      const categoryResponse = await productService.getCategories({ menu: menuId });
      if (categoryResponse?.data?.success) {
        setCategories([...(categoryResponse.data.data || [])]);
      }
    } catch (err) {
      console.error('Refresh categories failed:', err);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Category name is required');
      return;
    }

    if (!selectedBranch?.id || selectedBranch?.isAllOutlets) {
      toast.error('Please set a specific branch before adding categories');
      return;
    }

    setSavingCategory(true);
    try {
      const response = await productService.createCategory({
        name: newCategoryName.trim(),
        branch: selectedBranch.id,
        menu: menuId,
      });

      if (response?.data?.success) {
        toast.success('Category added');
        setNewCategoryName('');
        setCategoryDialogOpen(false);
        await refreshCategories();
      } else {
        toast.error(response?.data?.message || 'Failed to create category');
      }
    } catch (err) {
      console.error('Create category error:', err);
      toast.error('Failed to create category');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleEditCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Category name is required');
      return;
    }

    setSavingCategory(true);
    try {
      const response = await productService.updateCategory(editingCategory.id, {
        name: newCategoryName.trim(),
      });

      if (response?.data?.success) {
        toast.success('Category updated');
        setNewCategoryName('');
        setCategoryDialogOpen(false);
        setEditingCategory(null);
        await refreshCategories();
      } else {
        toast.error(response?.data?.message || 'Failed to update category');
      }
    } catch (err) {
      console.error('Update category error:', err);
      toast.error('Failed to update category');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    setDeletingItem(categoryId);
    try {
      const response = await productService.deleteCategory(categoryId);
      if (response.status === 204 || (response?.data?.success)) {
        toast.success('Category deleted');
        await refreshCategories();
      } else {
        toast.error(response?.data?.message || 'Failed to delete category');
      }
    } catch (err) {
      console.error('Delete category error:', err);
      toast.error('Failed to delete category');
    } finally {
      setDeletingItem(null);
    }
  };

  const handleToggleCategory = async (category) => {
    setTogglingItem(category.id);
    try {
      const response = await productService.toggleCategoryStatus(category.id);
      if (response?.data?.success) {
        toast.success(`Category ${category.is_active ? 'deactivated' : 'activated'}`);
        await refreshCategories();
      } else {
        toast.error(response?.data?.message || 'Failed to toggle category status');
      }
    } catch (err) {
      console.error('Toggle category error:', err);
      toast.error('Failed to toggle category status');
    } finally {
      setTogglingItem(null);
    }
  };

  const fetchMenusForCategoryCopy = async () => {
    try {
      const menusResponse = await productService.getMenus();
      if (menusResponse?.data?.success) {
        setAvailableMenusForCopy(
          (menusResponse.data.data || []).filter((menu) => menu.id !== Number(menuId))
        );
      }
    } catch (err) {
      console.error('Fetch menus failed:', err);
    }
  };

  const openCopyCategoryDialog = async (category) => {
    setCopyCategorySource(category);
    setCopyCategoryDialogOpen(true);
    await fetchMenusForCategoryCopy();
  };

  const closeCopyCategoryDialog = () => {
    setCopyCategoryDialogOpen(false);
    setCopyCategorySource(null);
    setCopyCategoryTargetMenu('');
    setCopyCategoryOperation('copy');
  };

  const handleCopyCategory = async () => {
    if (!copyCategoryTargetMenu) {
      toast.error('Select a target menu');
      return;
    }

    setCopyingCategory(true);
    try {
      const response = await productService.copyCategory(copyCategorySource.id, {
        menu: copyCategoryTargetMenu,
        operation: copyCategoryOperation,
      });

      if (response?.data?.success) {
        const operationText = copyCategoryOperation === 'move' ? 'moved' : 'copied';
        toast.success(`Category ${operationText} successfully with ${response.data.data.products_count} dishes`);
        closeCopyCategoryDialog();
        await refreshCategories();
      } else {
        toast.error(response?.data?.message || 'Failed to copy category');
      }
    } catch (err) {
      console.error('Copy category error:', err);
      toast.error('Failed to copy category');
    } finally {
      setCopyingCategory(false);
    }
  };

  const openEditCategoryDialog = (category) => {
    setEditingCategory(category);
    setNewCategoryName(category.name);
    setCategoryDialogOpen(true);
  };

  const closeCategoryDialog = () => {
    setCategoryDialogOpen(false);
    setEditingCategory(null);
    setNewCategoryName('');
  };

  const branchLabel = selectedBranch?.name || 'All Outlets';

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 360 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#FAFAFA', p: { xs: 2, md: 3 } }}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 4,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
          color: 'white',
          boxShadow: `0 8px 32px ${colors.primary.main}20`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(10px)',
          }
        }}
      >
        <Box sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          gap: 3,
          alignItems: 'center'
        }}>
          <Box sx={{ flex: 1, minWidth: 300 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <IconButton
                onClick={() => navigate('/dish-access')}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.3s ease-in-out'
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.75rem', md: '2rem' },
                  background: 'linear-gradient(45deg, #FFFFFF 30%, rgba(255,255,255,0.8) 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                {menuType?.name || 'Menu'} Categories
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <Chip
                icon={<StoreIcon />}
                label={`Branch: ${branchLabel}`}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  fontWeight: 500,
                  backdropFilter: 'blur(10px)'
                }}
              />
              <Chip
                icon={<OrderTypeIcon />}
                label={`Menu Type: ${menuType?.name || 'Menu'}`}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  fontWeight: 500,
                  backdropFilter: 'blur(10px)'
                }}
              />
            </Box>

            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255,255,255,0.9)',
                maxWidth: 600,
                lineHeight: 1.6,
                fontSize: '1rem'
              }}
            >
              Manage categories for your {menuType?.name?.toLowerCase() || 'menu'}.
              Click on a category to view and manage its dishes.
            </Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            alignItems: 'flex-start',
            minWidth: { xs: '100%', sm: 'auto' }
          }}>
            <Button
              variant="outlined"
              startIcon={<CategoryIcon />}
              onClick={() => setCategoryDialogOpen(true)}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.4)',
                borderWidth: 2,
                px: 3,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: '0.95rem',
                textTransform: 'none',
                backdropFilter: 'blur(10px)',
                backgroundColor: 'rgba(255,255,255,0.05)',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                },
                transition: 'all 0.3s ease-in-out'
              }}
            >
              Add Category
            </Button>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {categories.length === 0 ? (
        <Box sx={{
          py: { xs: 8, md: 12 },
          px: { xs: 3, md: 6 },
          textAlign: 'center',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          borderRadius: 4,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          border: '1px solid',
          borderColor: 'grey.200',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3
          }
        }}>
          <Box sx={{
            position: 'relative',
            zIndex: 1
          }}>
            {/* Animated Icon Container */}
            <Box sx={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #607D8B 0%, #546E7A 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 32px',
              boxShadow: '0 8px 32px rgba(96, 125, 139, 0.3)',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { transform: 'scale(1)', boxShadow: '0 8px 32px rgba(96, 125, 139, 0.3)' },
                '50%': { transform: 'scale(1.05)', boxShadow: '0 12px 40px rgba(96, 125, 139, 0.4)' },
                '100%': { transform: 'scale(1)', boxShadow: '0 8px 32px rgba(96, 125, 139, 0.3)' }
              }
            }}>
              <CategoryIcon sx={{
                fontSize: 60,
                color: 'white',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
              }} />
            </Box>

            {/* Main Heading */}
            <Typography
              variant="h4"
              sx={{
                mb: 3,
                fontWeight: 700,
                background: 'linear-gradient(135deg, #37474F 0%, #263238 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                lineHeight: 1.2
              }}
            >
              No Categories Yet
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                mb: 4,
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.7,
                fontSize: '1.1rem',
                color: 'text.secondary',
                fontWeight: 400
              }}
            >
              Categories help you organize your menu items into logical groups.
              Create your first category to start building your menu structure.
            </Typography>

            {/* Action Button */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCategoryDialogOpen(true)}
              sx={{
                background: 'linear-gradient(135deg, #607D8B 0%, #546E7A 100%)',
                color: 'white',
                px: 6,
                py: 2,
                borderRadius: 3,
                fontWeight: 600,
                fontSize: '1.1rem',
                textTransform: 'none',
                boxShadow: '0 4px 20px rgba(96, 125, 139, 0.4)',
                border: '2px solid transparent',
                '&:hover': {
                  background: 'linear-gradient(135deg, #546E7A 0%, #455A64 100%)',
                  boxShadow: '0 8px 30px rgba(96, 125, 139, 0.5)',
                  transform: 'translateY(-2px)',
                  borderColor: 'rgba(255,255,255,0.2)'
                },
                '&:active': {
                  transform: 'translateY(0)'
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              Create Your First Category
            </Button>

            {/* Additional Help Text */}
            <Typography
              variant="body2"
              sx={{
                mt: 3,
                color: 'text.secondary',
                fontSize: '0.9rem'
              }}
            >
              💡 Tip: Use descriptive names like "Appetizers", "Main Courses", or "Beverages"
            </Typography>
          </Box>
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {categories.map((category) => (
            <Grid item xs={12} sm={6} md={3.5} lg={3} xl={2.8} key={category.id}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  border: '1px solid',
                  borderColor: category.is_active ? '#E0E0E0' : '#F5F5F5',
                  backgroundColor: category.is_active ? 'white' : '#FAFAFA',
                  transition: 'all 0.2s ease-in-out',
                  cursor: 'pointer',
                  position: 'relative',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    borderColor: category.is_active ? '#607D8B' : '#E0E0E0',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <CardActionArea
                  onClick={() => navigate(`/dish-access/${menuId}/categories/${category.id}`)}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    p: 0
                  }}
                >
                  <CardContent sx={{
                    p: 2.5,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    '&:last-child': { pb: 2.5 }
                  }}>
                    {/* Category Name */}
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: category.is_active ? '#37474F' : '#9E9E9E',
                          fontSize: '1rem',
                          lineHeight: 1.4,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {category.name}
                      </Typography>
                    </Box>

                    {/* Info Row */}
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      pt: 2,
                      borderTop: '1px solid #F0F0F0'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            backgroundColor: category.is_active ? '#F3E5F5' : '#F5F5F5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: category.is_active ? '#7B1FA2' : '#BDBDBD',
                              fontSize: '0.75rem'
                            }}
                          >
                            {category.dish_count || 0}
                          </Typography>
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: category.is_active ? '#607D8B' : '#9E9E9E',
                            fontSize: '0.8rem',
                            fontWeight: 500
                          }}
                        >
                          {category.dish_count === 1 ? 'dish' : 'dishes'}
                        </Typography>
                      </Box>

                      <Switch
                        checked={category.is_active}
                        onChange={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleToggleCategory(category);
                        }}
                        disabled={togglingItem === category.id}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#4CAF50',
                            '& + .MuiSwitch-track': {
                              backgroundColor: '#4CAF50',
                            }
                          }
                        }}
                      />
                    </Box>
                  </CardContent>
                </CardActionArea>

                {/* Action Buttons Overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    display: 'flex',
                    gap: 0.5,
                    opacity: 0,
                    transition: 'opacity 0.2s ease-in-out',
                    '&:hover': { opacity: 1 },
                    '.MuiCard-root:hover &': { opacity: 1 }
                  }}
                >
                  <Tooltip title="Edit" placement="top">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        openEditCategoryDialog(category);
                      }}
                      sx={{
                        bgcolor: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                        color: '#607D8B',
                        width: 32,
                        height: 32,
                        '&:hover': {
                          bgcolor: '#607D8B',
                          color: 'white'
                        }
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete" placement="top">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDeleteCategory(category.id);
                      }}
                      disabled={deletingItem === category.id}
                      sx={{
                        bgcolor: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                        color: '#F44336',
                        width: 32,
                        height: 32,
                        '&:hover': {
                          bgcolor: '#F44336',
                          color: 'white'
                        }
                      }}
                    >
                      {deletingItem === category.id ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <DeleteIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Category Dialog */}
      <Dialog
        open={categoryDialogOpen}
        onClose={closeCategoryDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 12px 40px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{
          p: 3,
          pb: 2,
          backgroundColor: '#FAFAFA',
          borderBottom: '1px solid #E0E0E0',
          fontWeight: 700,
          fontSize: '1.1rem',
          color: '#37474F'
        }}>
          {editingCategory ? 'Edit Category' : 'Add New Category'}
        </DialogTitle>

        <DialogContent sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5
        }}>
          {/* Description */}
          <Typography
            variant="body2"
            sx={{
              color: '#607D8B',
              fontSize: '0.9rem',
              mb: 1
            }}
          >
            {editingCategory 
              ? 'Update the category name to keep your menu organized.'
              : 'Create a new category to organize related dishes together.'}
          </Typography>

          {/* Category Name Input */}
          <TextField
            label="Category Name"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            placeholder="e.g., Appetizers, Main Course, Desserts"
            fullWidth
            autoFocus
            variant="outlined"
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '0.95rem',
                '&:hover fieldset': {
                  borderColor: '#607D8B',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#607D8B',
                  borderWidth: '2px'
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.95rem'
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#607D8B',
              },
            }}
          />

          {/* Handle for specifically branch cases */}
          {(!selectedBranch?.id || selectedBranch?.isAllOutlets) && !editingCategory && (
            <Alert
              severity="warning"
              sx={{
                borderRadius: 1.5,
                backgroundColor: '#FFF3E0',
                color: '#E65100',
                fontSize: '0.85rem',
                '& .MuiAlert-icon': {
                  color: '#E65100',
                },
              }}
            >
              Please select a specific branch to add a category.
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{
          p: 3,
          pt: 2,
          display: 'flex',
          gap: 1.5,
          borderTop: '1px solid #E0E0E0'
        }}>
          <Button
            onClick={closeCategoryDialog}
            variant="text"
            sx={{
              color: '#607D8B',
              fontWeight: 500,
              textTransform: 'none',
              px: 3,
              py: 1,
              '&:hover': {
                backgroundColor: '#F5F5F5',
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={editingCategory ? handleEditCategory : handleCreateCategory}
            variant="contained"
            disabled={savingCategory || !newCategoryName.trim() || ((!selectedBranch?.id || selectedBranch?.isAllOutlets) && !editingCategory)}
            sx={{
              backgroundColor: '#607D8B',
              color: 'white',
              fontWeight: 600,
              textTransform: 'none',
              px: 4,
              py: 1,
              borderRadius: 1.5,
              minWidth: 120,
              '&:hover': {
                backgroundColor: '#546E7A',
              },
              '&:disabled': {
                backgroundColor: '#BDBDBD',
                color: '#9E9E9E'
              }
            }}
          >
            {savingCategory ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Saving...
              </Box>
            ) : (
              editingCategory ? 'Update' : 'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Category Dialog */}
      <Dialog
        open={copyCategoryDialogOpen}
        onClose={closeCopyCategoryDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#F5F5F5',
          color: '#37474F',
          fontWeight: 600,
          fontSize: '1.25rem',
          pb: 2,
          borderBottom: '1px solid #E0E0E0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CopyIcon sx={{ color: '#1976D2' }} />
            Copy/Move Category
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, maxHeight: 'calc(100vh - 250px)', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {copyCategorySource && (
              <Alert
                severity="info"
                sx={{
                  borderRadius: 2,
                  backgroundColor: '#E3F2FD',
                  color: '#0D47A1',
                  '& .MuiAlert-icon': {
                    color: '#0D47A1',
                  },
                }}
              >
                <strong>Source:</strong> {copyCategorySource.name}
              </Alert>
            )}

            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 600, color: '#37474F', mb: 2 }}>
                Operation Type
              </FormLabel>
              <RadioGroup
                row
                value={copyCategoryOperation}
                onChange={(e) => setCopyCategoryOperation(e.target.value)}
                sx={{ gap: 3 }}
              >
                <FormControlLabel
                  value="copy"
                  control={<Radio sx={{ color: '#1976D2', '&.Mui-checked': { color: '#1976D2' } }} />}
                  label="Copy Category"
                  sx={{ color: '#37474F' }}
                />
                <FormControlLabel
                  value="move"
                  control={<Radio sx={{ color: '#FF9800', '&.Mui-checked': { color: '#FF9800' } }} />}
                  label="Move Category"
                  sx={{ color: '#37474F' }}
                />
              </RadioGroup>
            </FormControl>

            <FormControl fullWidth variant="outlined">
              <InputLabel sx={{ color: '#607D8B', '&.Mui-focused': { color: '#1976D2' } }}>
                Target Menu Type
              </InputLabel>
              <Select
                value={copyCategoryTargetMenu}
                onChange={(e) => setCopyCategoryTargetMenu(e.target.value)}
                label="Target Menu Type"
                sx={{
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E0E0E0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976D2',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976D2',
                  },
                }}
              >
                {availableMenusForCopy.map((menu) => (
                  <MenuItem key={menu.id} value={menu.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RestaurantMenuIcon sx={{ fontSize: 18, color: '#607D8B' }} />
                      {menu.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Alert
              severity={copyCategoryOperation === 'move' ? 'warning' : 'info'}
              sx={{
                borderRadius: 2,
                backgroundColor: copyCategoryOperation === 'move' ? '#FFF3E0' : '#E3F2FD',
                color: copyCategoryOperation === 'move' ? '#FF9800' : '#0D47A1',
                '& .MuiAlert-icon': {
                  color: copyCategoryOperation === 'move' ? '#FF9800' : '#0D47A1',
                },
              }}
            >
              {copyCategoryOperation === 'move'
                ? 'Moving will permanently remove the category from the current menu and all its dishes.'
                : 'Copying will create a duplicate of the category and all its dishes in the target menu.'
              }
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, gap: 2 }}>
          <Button
            onClick={closeCopyCategoryDialog}
            sx={{
              color: '#607D8B',
              fontWeight: 500,
              textTransform: 'none',
              px: 3,
              '&:hover': {
                backgroundColor: '#F5F5F5',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCopyCategory}
            variant="contained"
            disabled={copyingCategory || !copyCategoryTargetMenu}
            sx={{
              backgroundColor: copyCategoryOperation === 'move' ? '#FF9800' : '#1976D2',
              color: 'white',
              fontWeight: 500,
              textTransform: 'none',
              px: 3,
              py: 1.5,
              borderRadius: 2,
              boxShadow: `0 2px 8px ${copyCategoryOperation === 'move' ? 'rgba(255,152,0,0.3)' : 'rgba(25,118,210,0.3)'}`,
              '&:hover': {
                backgroundColor: copyCategoryOperation === 'move' ? '#F57C00' : '#1565C0',
                boxShadow: `0 4px 12px ${copyCategoryOperation === 'move' ? 'rgba(255,152,0,0.4)' : 'rgba(25,118,210,0.4)'}`,
              },
              '&:disabled': {
                backgroundColor: '#BDBDBD',
                color: '#9E9E9E',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            {copyingCategory ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                {copyCategoryOperation === 'move' ? 'Moving...' : 'Copying...'}
              </Box>
            ) : (
              copyCategoryOperation === 'move' ? 'Move Category' : 'Copy Category'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MenuCategories;